import { SourcingChannelStatus } from 'api-clients/monolith';
import {
  Info,
  MoreHorizontal,
  CirclePause,
  Pencil,
  CirclePlay,
} from 'lucide-react';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { Button } from '@/components/ui/button';
import {
  RowDropdownMenu as DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Typography } from '@/components/ui/typography';
import { useToggleCampaign } from '@/hooks/use-toggle-campaign';

import { SetReachPublisherStatus } from './use-reach-channels';

interface CampaignMenuProps {
  sourcingPurchaseId: string;
  reachCampaignId: string | null;
  status: SourcingChannelStatus;
  channelSlug: string;
  onEdit?: () => void;
  setReachPublisherStatus?: SetReachPublisherStatus;
  isDisabled?: boolean;
  showPauseResume?: boolean;
}

export function CampaignMenu({
  sourcingPurchaseId,
  reachCampaignId,
  status,
  channelSlug,
  onEdit,
  setReachPublisherStatus,
  isDisabled,
  showPauseResume,
}: CampaignMenuProps) {
  const [open, setOpen] = useState(false);
  const { handleToggle, isCampaignActive } = useToggleCampaign({
    sourcingPurchaseId,
    reachCampaignId,
    status,
    channel: channelSlug,
    setReachPublisherStatus,
  });

  const showDetails = status === 'completed' || status === 'failed';
  const showEdit = !showDetails;

  if (!showPauseResume && !showEdit && !showDetails) return null;

  const handlePauseResume = () => {
    setOpen(false);
    setTimeout(() => handleToggle(), 0);
  };

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild disabled={isDisabled}>
        <Button variant="icon" size="icon" disabled={isDisabled}>
          <MoreHorizontal className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {showPauseResume && (
          <DropdownMenuItem onClick={handlePauseResume}>
            {isCampaignActive ? (
              <>
                <CirclePause className="text-fx-neutral-400 h-4 w-4" />
                <Typography variant="subtitle1">
                  <FormattedMessage
                    defaultMessage="Pause campaign"
                    description="Campaign row menu: pause an active campaign"
                  />
                </Typography>
              </>
            ) : (
              <>
                <CirclePlay className="text-fx-neutral-400 h-4 w-4" />
                <Typography variant="subtitle1">
                  <FormattedMessage
                    defaultMessage="Resume campaign"
                    description="Campaign row menu: resume a paused campaign"
                  />
                </Typography>
              </>
            )}
          </DropdownMenuItem>
        )}

        {showEdit && (
          <DropdownMenuItem onClick={onEdit}>
            <Pencil className="text-fx-neutral-400 h-4 w-4" />
            <Typography variant="subtitle1">
              <FormattedMessage
                defaultMessage="Edit campaign"
                description="Campaign row menu: open campaign settings to edit"
              />
            </Typography>
          </DropdownMenuItem>
        )}

        {showDetails && (
          <DropdownMenuItem onClick={onEdit}>
            <Info className="text-fx-neutral-400 h-4 w-4" />
            <Typography variant="subtitle1">
              <FormattedMessage
                defaultMessage="Campaign details"
                description="Campaign row menu: view campaign details for a completed campaign"
              />
            </Typography>
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
